import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Grab-to-pan for a zoomed document pane: press on the page and drag to scroll the
 * container, like a map or a PDF viewer's hand tool. Pointer events, so mouse, pen and
 * touch all work. Only meaningful when the content overflows (zoom > 100%), so the caller
 * passes `enabled` and the hook is a no-op otherwise — at 100% a plain wheel scroll is
 * enough and a grab cursor would be misleading.
 */
export function usePanToScroll(enabled: boolean) {
  const drag = useRef<{ id: number; x: number; y: number; left: number; top: number } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled || e.button !== 0) return;
      const el = e.currentTarget;
      drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
      el.setPointerCapture?.(e.pointerId);
      el.dataset.panning = 'true';
    },
    [enabled],
  );

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const el = e.currentTarget;
    el.scrollLeft = d.left - (e.clientX - d.x);
    el.scrollTop = d.top - (e.clientY - d.y);
    e.preventDefault();
  }, []);

  const end = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current || drag.current.id !== e.pointerId) return;
    drag.current = null;
    const el = e.currentTarget;
    el.releasePointerCapture?.(e.pointerId);
    delete el.dataset.panning;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp: end, onPointerCancel: end };
}
